import { Link } from 'react-router-dom';

const tempStaffingCards = [
  { title: 'Fast Turnaround', desc: 'Placements coordinated immediately when your need arises.' },
  { title: 'Pre-Screened Candidates', desc: 'Every worker is vetted and matched to your specific role requirements before we make a recommendation.' },
  { title: 'Flexible Assignment Lengths', desc: 'From a single day to several months - we scale with your needs, not the other way around.' },
  { title: 'Hands-Off Administration', desc: 'We manage payroll, compliance, and paperwork for every temporary placement so you don\'t have to.' },
  { title: 'Ongoing Support', desc: 'We stay engaged throughout the assignment - checking in with you and the worker to make sure everything is running smoothly.' },
  { title: 'Broad Industry Reach', desc: 'Our candidate network spans a wide range of industries and skill sets, so we can find the right fit no matter your field.' },
];

const tempToHireCards = [
  { title: 'Reduce Hiring Risk', desc: 'Evaluate real performance in your environment before making a permanent offer.' },
  { title: 'Seamless Conversion', desc: 'When you\'re ready to hire, the transition is simple. The candidate already knows your team and processes.' },
  { title: 'No Up-Front Commitment', desc: 'We handle payroll and compliance during the temporary period, so you stay flexible until you\'re sure.' },
  { title: 'Flexible Timelines', desc: 'Conversion schedules are tailored to your hiring process. Move at the pace that works for you.' },
  { title: 'Motivated Candidates', desc: 'Temp-to-hire placements attract candidates who are eager to prove themselves and land a permanent role, bringing extra drive from day one.' },
  { title: 'Lower Turnover', desc: 'Making the right hire the first time saves you the cost and disruption of replacing someone who wasn\'t the right fit.' },
];

function ServiceBlock({
  icon,
  tag,
  title,
  lead,
  body,
  cards,
  alt,
}: {
  icon: string;
  tag: string;
  title: string;
  lead: string;
  body: string;
  cards: { title: string; desc: string }[];
  alt?: boolean;
}) {
  return (
    <section className={`py-20 ${alt ? 'bg-[#f8f7f2]' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto px-8 md:px-14">
        {/* Header */}
        <div className="border border-gray-100 rounded-lg overflow-hidden mb-10">
          <div className="bg-[#1a3a3a] px-8 py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${icon} text-xl text-[#c8a84b]`} />
              </div>
              <h2 className="text-white font-extrabold text-xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>{title}</h2>
            </div>
            <span className="px-3 py-1 rounded-sm bg-[#c8a84b]/20 text-[#c8a84b] text-xs font-bold tracking-wide whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>{tag}</span>
          </div>
          <div className="px-8 py-7">
            <p className="text-[#1a3a3a] font-semibold text-base mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>{lead}</p>
            <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{body}</p>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c) => (
            <div key={c.title} className="bg-white rounded-lg border border-gray-100 p-6 hover:border-[#c8a84b]/40 transition-all duration-200">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-check-line text-[#c8a84b] text-base shrink-0" />
                <h3 className="text-sm font-extrabold text-[#1a3a3a]" style={{ fontFamily: "'Montserrat', sans-serif" }}>{c.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="bg-[#1a3a3a] py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full overflow-hidden pointer-events-none">
          <div
            className="absolute top-0 right-0 w-full h-full opacity-10"
            style={{ background: 'repeating-linear-gradient(135deg, #c8a84b 0px, #c8a84b 2px, transparent 2px, transparent 24px)' }}
          />
        </div>
        <div className="absolute top-0 left-0 w-1 h-full bg-[#c8a84b]" />
        <div className="max-w-6xl mx-auto px-8 md:px-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#c8a84b]" />
            <span className="text-[#c8a84b] text-xs font-bold tracking-[0.3em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>What We Offer</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Our Services
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl" style={{ fontFamily: "'Inter', sans-serif" }}>
            Every business is different. Tell us what you need and we&apos;ll tailor a plan that works for you.
          </p>
        </div>
      </section>

      <ServiceBlock
        icon="ri-time-line"
        tag="Flexible"
        title="Temporary Staffing"
        lead="When your business needs fast, flexible, and reliable support - we're ready to deliver."
        body="Whether you're navigating a seasonal surge, covering an unexpected absence, or scaling up for a time-sensitive project, Continuous Staffing provides qualified workers on short notice. We maintain an active pool of pre-screened candidates across a range of industries and skill sets, so you spend less time searching and more time getting the job done. Our temporary staffing solutions are built around your schedule and requirements. We handle recruitment, screening, onboarding paperwork, and payroll for every temporary placement - giving you the workforce flexibility you need without the administrative overhead."
        cards={tempStaffingCards}
        alt={false}
      />

      <ServiceBlock
        icon="ri-briefcase-line"
        tag="Low Risk"
        title="Temp-to-Hire"
        lead="Try before you commit. Find your next long-term team member with confidence."
        body="Hiring a permanent employee is a big decision. Our temp-to-hire model gives you the opportunity to evaluate a candidate's skills, work ethic, and cultural fit in a real work environment before extending a full-time offer. It's a low-risk, high-confidence path to building a great team. We place the candidate with you on a temporary basis first. During this period, you work with them day-to-day and assess whether they're the right long-term fit. When you're ready to bring them on permanently, the transition is straightforward. No guesswork, no surprises."
        cards={tempToHireCards}
        alt
      />

      {/* CTA */}
      <section className="py-20 bg-[#1a3a3a] text-center">
        <div className="max-w-3xl mx-auto px-8">
          <h2 className="text-4xl font-extrabold text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Let&apos;s Find the Right Solution
          </h2>
          <p className="text-white/65 text-lg mb-10" style={{ fontFamily: "'Inter', sans-serif" }}>
            Every business is different. Tell us what you need and we&apos;ll tailor a plan that works for you.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#c8a84b] text-white font-bold text-sm tracking-wide hover:bg-[#b8943b] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Get a Free Consultation <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </main>
  );
}
